import { PrivateAmount } from '@/components/ui/PrivateAmount'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatCompactEuros, formatEuros, formatMonthYear } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { MonthlyRevenuePoint } from '@/lib/report-types'

const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

/**
 * Concept in dezelfde accentkleur, maar open in plaats van dicht: het is hetzelfde geld,
 * alleen nog niet vast. Een eigen kleur zou het als een andere soort omzet laten lezen.
 */
export const DRAFT_FILL = 'bg-accent-600/20 ring-1 ring-inset ring-accent-600/45'

export function Legend({ year, points }: { year: number; points?: MonthlyRevenuePoint[] }) {
  // Ook een negatief conceptbedrag (een creditnota staat als concept klaar) verdient
  // het legenda-item: anders staat er straks een bedrag in beeld dat niets verklaart.
  const hasDraft = (points ?? []).some((point) => toNumber(point.draft) !== 0)

  return (
    <div className="flex items-center gap-3 text-xs text-ink-faint">
      <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-xs bg-accent-600" /> {year}</span>
      {hasDraft ? (
        <span className="inline-flex items-center gap-1.5"><span className={cn('h-2 w-2 rounded-xs', DRAFT_FILL)} /> concept</span>
      ) : null}
      <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-xs bg-(--chart-prev)" /> {year - 1}</span>
    </div>
  )
}

/**
 * Omzet per maand, dit jaar tegen vorig jaar. Elke kolom is een schakelaar naar de
 * uitsplitsing van die maand; nogmaals klikken zet hem weer uit.
 *
 * Het maandlabel zit bewust in de kolom en niet in een eigen rij eronder: zo is het
 * onderdeel van het klikdoel (op 375px is een kolom nog geen twintig pixel breed) en
 * kan het als selectiesignaal meebewegen.
 */
export function MonthlyChart({
  points,
  year,
  selectedMonth,
  onSelectMonth,
}: {
  points: MonthlyRevenuePoint[]
  year: number
  selectedMonth: number | null
  onSelectMonth: (month: number | null) => void
}) {
  // Het concept stapelt op de omzet, dus de schaal moet die som aankunnen. Alleen een
  // positief conceptdeel telt daarin mee: een negatief bedrag (een creditnota die als
  // concept klaarstaat) wordt niet getekend en zou de noemer onder de omzetstaaf duwen,
  // waarna die over de honderd procent gaat en de y-as niet meer klopt.
  const max = Math.max(1, ...points.flatMap((m) => [toNumber(m.current) + Math.max(0, toNumber(m.draft)), toNumber(m.previous)]))

  return (
    <div className="mt-6 flex gap-3">
      <div className="flex h-48 w-9 flex-col justify-between text-right">
        <PrivateAmount className="money text-[10px] leading-none text-ink-faint" reveal="none">{formatCompactEuros(max)}</PrivateAmount>
        <PrivateAmount className="money text-[10px] leading-none text-ink-faint" reveal="none">{formatCompactEuros(max / 2)}</PrivateAmount>
        <span className="money text-[10px] leading-none text-ink-faint">0</span>
      </div>
      <div className="relative min-w-0 flex-1">
        <div className="pointer-events-none absolute inset-x-0 top-0 flex h-48 flex-col justify-between">
          <div className="border-t border-hairline" />
          <div className="border-t border-hairline" />
          <div className="border-t border-hairline" />
        </div>
        <div className="relative flex items-stretch gap-1">
          {points.map((month, i) => {
            const current = toNumber(month.current)
            const previous = toNumber(month.previous)
            const draft = toNumber(month.draft)
            const selected = selectedMonth === month.month
            const dimmed = selectedMonth !== null && !selected
            return (
              // De kolom heeft nu zelf een klik-actie; met tapToToggle zou een tik zowel
              // de maand kiezen als de tooltip togglen. Op touch is het paneel de uitleg.
              <Tooltip
                key={month.month}
                className="whitespace-nowrap"
                tapToToggle={false}
                content={
                  <span className="flex flex-col gap-0.5">
                    <span className="font-semibold text-ink">{MONTHS[month.month - 1]}</span>
                    <PrivateAmount className="money text-ink-soft" reveal="none"><span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-accent-600" />{year}: {formatEuros(current)}</PrivateAmount>
                    {draft !== 0 ? (
                      <PrivateAmount className="money text-ink-soft" reveal="none"><span className={cn('mr-1 inline-block h-1.5 w-1.5 rounded-full', DRAFT_FILL)} />concept: {formatEuros(draft)}</PrivateAmount>
                    ) : null}
                    <PrivateAmount className="money text-ink-faint" reveal="none"><span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-(--chart-prev)" />{year - 1}: {formatEuros(previous)}</PrivateAmount>
                  </span>
                }
              >
                {/* Alles hierbinnen is een span: een div mag niet in een button staan. */}
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelectMonth(selected ? null : month.month)}
                  className={cn(
                    'group flex flex-1 flex-col rounded-md pb-1 transition-colors duration-150 ease-spring',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
                    selected ? 'bg-fill-2' : 'hover:bg-fill-1',
                  )}
                >
                  {/* Beide staven dempen als een geheel, zodat hun onderlinge verhouding blijft staan. */}
                  <span
                    className={cn(
                      'flex h-48 w-full items-end justify-center gap-0.5 transition-opacity duration-150 ease-spring',
                      dimmed && 'opacity-60 group-hover:opacity-100',
                    )}
                  >
                    {/* Het concept stapelt bovenop de omzet van dezelfde maand: samen de
                        volle staaf, en het open vlak toont wat daarvan nog moet landen. */}
                    <span
                      className="flex h-full w-2 max-w-[8px] origin-bottom animate-grow-bar flex-col justify-end"
                      style={{ animationDelay: `${i * 30}ms` }}
                    >
                      {draft > 0 ? (
                        <span className={cn('rounded-t-[2px]', DRAFT_FILL)} style={{ height: `${(draft / max) * 100}%` }} />
                      ) : null}
                      <span
                        className={cn('bg-accent-600', draft > 0 ? 'rounded-t-none' : 'rounded-t-[2px]')}
                        style={{ height: `${(current / max) * 100}%` }}
                      />
                    </span>
                    <span
                      className="w-1 max-w-[4px] origin-bottom animate-grow-bar rounded-t-[2px] bg-(--chart-prev)"
                      style={{ height: `${(previous / max) * 100}%`, animationDelay: `${i * 30 + 60}ms` }}
                    />
                  </span>
                  <span
                    className={cn(
                      'mt-2 text-center text-[10px] leading-none transition-colors duration-150',
                      selected ? 'font-semibold text-ink' : 'text-ink-faint group-hover:text-ink-soft',
                    )}
                  >
                    {MONTHS[month.month - 1]}
                    {/* Aanvulling in plaats van aria-label: die zou de zichtbare tekst
                        overschrijven, en "mrt" komt niet voor in "Maart 2026". */}
                    <span className="sr-only"> {formatMonthYear(month.month, year)}, uitsplitsing</span>
                  </span>
                </button>
              </Tooltip>
            )
          })}
        </div>
      </div>
    </div>
  )
}
